<div class="payment-history-container">
  <!-- Breadcrumbs -->
  <div class="breadcrumbs-section">
    <div class="breadcrumbs">
      <span class="breadcrumb-link" (click)="navigateBack()">Orders</span>
      <span class="breadcrumb-separator">></span>
      <span class="breadcrumb-link">Order #{{ order?.order_number || orderId }}</span>
      <span class="breadcrumb-separator">></span>
      <span class="breadcrumb-current">Payment History</span>
    </div>
  </div>

  <!-- Order Summary Header -->
  <div class="order-summary-section">
    <div class="order-summary-content">
      <!-- Order Info -->
      <div class="order-info-content">
        <div class="order-table-badge">
          @if (order?.order_type == 'togo' || order?.order_type == 'togo_dine_in') {
            ToGo
          } @else if (order?.online_platform_type && order?.order_type == 'online') {
            {{ order?.online_platform_type == 'forknfind' ? 'F&F' : order?.online_platform_type }}
          } @else if (order?.tables && order?.tables?.length > 0) {
            {{ order?.tables[0]?.table_name }}
          } @else {
            N/A
          }
        </div>
        <div class="order-info-right">
          <div class="order-date-time">
            {{ formatDateTime(order?.created_at) }}
          </div>
          <div class="customer-name">
            {{
              order?.customers && order.customers.length > 0
                ? order.customers[0].first_name + ' ' + (order.customers[0].last_name || '')
                : 'N/A'
            }}
          </div>
        </div>
      </div>

      <!-- Summary Boxes -->
      <div class="summary-boxes-container">
        <div class="summary-box payment-summary-box">
          <div class="summary-label">
            ORDER TOTAL <span class="summary-count">({{ payments.length }})</span>
          </div>
          <div class="summary-amount payment-amount">${{ order?.total.toFixed(2) }}</div>
        </div>
        <div class="summary-box refund-summary-box">
          <div class="summary-label">
            TOTAL REFUND <span class="summary-count">({{ getPaymentsWithRefundsCount() }})</span>
          </div>
          <div class="summary-amount refund-amount">-${{ order?.total_refund.toFixed(2) }}</div>
        </div>
        <div class="summary-box net-total-summary-box">
          <div class="summary-label">NET TOTAL</div>
          <div class="summary-amount net-total-amount">
            ${{ order?.total_after_refund.toFixed(2) }}
          </div>
        </div>
      </div>
    </div>
    <div class="order-info-divider"></div>
  </div>

  <!-- Payment History Heading with Navigation -->
  <div class="payment-history-heading-section">
    <h2 class="page-title">Payment History</h2>
    <div class="navigation-arrows">
      <button
        class="nav-arrow-btn"
        type="button"
        (click)="scrollLeft()"
        [disabled]="!canScrollLeftState"
      >
        <i class="bx bx-chevron-left"></i>
      </button>
      <button
        class="nav-arrow-btn"
        type="button"
        (click)="scrollRight()"
        [disabled]="!canScrollRightState"
      >
        <i class="bx bx-chevron-right"></i>
      </button>
    </div>
  </div>

  <!-- Payment Cards -->
  @if (payments && payments.length > 0) {
    <div class="payments-container" #paymentsContainer>
      @for (payment of payments; track payment; let paymentIndex = $index) {
        <div class="payment-card">
          <!-- Payment Header -->
          <div class="payment-header">
            <div class="payment-number">PAYMENT {{ paymentIndex + 1 }}</div>
            <div class="payment-customer">
              @let payer = getPaymentCustomer(payment?.user_id);
              <span class="customer-name-text">
                {{ payer?.first_name || 'N/A' }}
                {{ payer?.last_name || '' }}
              </span>
              @if (hasCustomerImage(payer)) {
                <img
                  [src]="payer.img_path.trim()"
                  [alt]="payer.first_name"
                  class="customer-avatar"
                  loading="lazy"
                  (error)="onCustomerImageError(payer)"
                />
              } @else {
                <div class="customer-avatar-initials">
                  {{ getCustomerInitials(getCustomerName(payment?.user_id)) }}
                </div>
              }
            </div>
          </div>

          <!-- Payment Details -->
          <div class="payment-details">
            <div class="detail-row">
              <span class="detail-label">Payment method:</span>
              <span class="detail-value">{{ payment.payment_mode || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Amount paid:</span>
              <span class="detail-value">${{ parseFloat(payment.paid_amount).toFixed(2) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Discount:</span>
              <span class="detail-value">${{ parseFloat(payment.discount).toFixed(2) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Tax Amount:</span>
              <span class="detail-value">${{ parseFloat(payment.tax_amount).toFixed(2) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Tip Amount:</span>
              <span class="detail-value">${{ parseFloat(payment.tip_amount).toFixed(2) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Balance:</span>
              <span class="detail-value balance-value"
                >${{ parseFloat(payment.balance).toFixed(2) }}</span
              >
            </div>
            <div class="detail-row">
              <span class="detail-label">Transaction ID:</span>
              <span class="detail-value">{{ payment.payment_ref_id || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Date:</span>
              <span class="detail-value">{{ formatDate(payment.paid_at) || '-' }}</span>
            </div>
          </div>

          <!-- Refund Notification -->
          @if (payment.refunds && payment.refunds.length > 0) {
            <div class="row custom-line-bottom m-0 mb-3"></div>
            <div class="refund-notification justify-content-center">
              <i class="bx bx-info-circle"></i>
              <span
                >{{ payment.refunds.length }} Refund{{
                  payment.refunds.length > 1 ? 's' : ''
                }}
                Issued To This Payment</span
              >
            </div>

            <!-- Refunds List -->
            <div class="refunds-list">
              @for (refund of payment.refunds; track refund; let refundIndex = $index) {
                <div class="refund-item">
                  <div class="refund-header" (click)="toggleRefund(paymentIndex, refundIndex)">
                    <span class="refund-number">REFUND {{ refundIndex + 1 }}</span>
                    <i
                      class="bx"
                      [class.bx-chevron-down]="isRefundExpanded(paymentIndex, refundIndex)"
                      [class.bx-chevron-up]="!isRefundExpanded(paymentIndex, refundIndex)"
                    ></i>
                  </div>

                  @if (isRefundExpanded(paymentIndex, refundIndex)) {
                    <div class="refund-details">
                      <div class="detail-row">
                        <span class="detail-label">Refund method:</span>
                        <span class="detail-value">{{
                          refund.refund_method || refund.payment_method || 'N/A'
                        }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Refund type:</span>
                        <span class="detail-value refund-type-value">{{
                          refund.refund_type || 'N/A'
                        }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Refund Amount:</span>
                        <span class="detail-value refund-amount-value"
                          >-${{ parseFloat(refund.refund_amount).toFixed(2) }}</span
                        >
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Reason:</span>
                        <span class="detail-value">{{ refund.reason || 'N/A' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Refund ID:</span>
                        <span class="detail-value">{{ refund.refund_id || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Date:</span>
                        <span class="detail-value">{{ formatDate(refund?.created_at) }}</span>
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Total Refund -->
              <div class="total-refund">
                Total Refund:
                <span class="total-refund-amount"
                  >-${{ getTotalRefundForPayment(payment).toFixed(2) }}</span
                >
              </div>
            </div>
          }
        </div>
      }
    </div>
  } @else {
    <div class="no-payments">
      <div
        class="d-flex flex-column align-items-center justify-content-center common-empty-container"
      >
        <img src="assets/no_payment.png" alt="No Devices" class="common-empty-img mb-3" />
        <p class="common-empty-header">Waiting for payments.</p>
        <p class="common-empty-sub-text">
          Payment details will appear here once payment is collected.
        </p>
      </div>
    </div>
  }
</div>
